// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useClawbackReceiverDelegations } from '@hooks/queries/registry/clawback/useClawbackReceiverDelegations';
import { useRegistrarHoldingOperationsDelegations } from '@hooks/queries/registry/registrar/useRegistrarHoldingOperationsDelegationsByParty';
import { useCallback, useMemo, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { ParticipantService } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Participant';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput from '../../../components/Form/TextInput';
import Loading from '../../../components/Loading';
import { useLedgerExercise } from '../../../hooks/mutations/ledger/useLedgerExercise';
import useMutate from '../../../hooks/mutations/useMutate';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import useServiceUpgradeConsents from '../../../hooks/queries/registry/onboarding/useServiceUpgradeConsents';
import useHoldings from '../../../hooks/queries/registry/useHoldings';
import { ERR_END_INVESTOR_OFFBOARDING, SUC_END_INVESTOR_OFFBOARDING } from '../../../utils/strings';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  participantServiceContractId: string;
  participant: string;
};

export const OffboardEndInvestorDialog = ({
  open,
  onClose,
  participantServiceContractId,
  participant,
}: DialogProps) => {
  const [endInvestor, setEndInvestor] = useState<string>('');
  const mutate = useMutate();
  const offboardEndInvestorMutation = useLedgerExercise(
    ParticipantService.ParticipantService_OffboardEndInvestor,
  );
  const credentials = useCredentials();
  const holdings = useHoldings();
  const clawbackDelegations = useClawbackReceiverDelegations();
  const registrarHoldingOperationsDelegations = useRegistrarHoldingOperationsDelegations();
  const serviceUpgradeConsents = useServiceUpgradeConsents();

  const resetState = useCallback(() => {
    setEndInvestor('');
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  // Filter credentials that belong to the end investor and are associated with this participant
  const endInvestorCredentials = useMemo(
    () =>
      credentials.data?.filter(
        (cred) => cred.payload.holder === endInvestor && cred.payload.participant === participant,
      ) ?? [],
    [credentials.data, endInvestor, participant],
  );

  const endInvestorClawbackDelegations = useMemo(
    () => clawbackDelegations.data?.filter((d) => d.payload.delegator === endInvestor) ?? [],
    [clawbackDelegations.data, endInvestor],
  );

  const endInvestorRegistrarHoldingDelegations = useMemo(
    () =>
      registrarHoldingOperationsDelegations.data?.filter(
        (d) => d.payload.delegator === endInvestor,
      ) ?? [],
    [registrarHoldingOperationsDelegations.data, endInvestor],
  );

  const endInvestorUpgradeConsents = useMemo(
    () => serviceUpgradeConsents.data?.filter((c) => c.payload.authorizer === endInvestor) ?? [],
    [serviceUpgradeConsents.data, endInvestor],
  );

  // Filter holdings owned by the end investor
  const endInvestorHoldings = useMemo(
    () => holdings.data?.filter((h) => h.payload.owner === endInvestor) ?? [],
    [holdings.data, endInvestor],
  );

  const offboardEndInvestor = useCallback(async () => {
    const credentialCids = endInvestorCredentials.map((cred) => cred.contractId);
    const payload = {
      cid: participantServiceContractId as ContractId<ParticipantService>,
      argument: {
        endInvestor,
        existingContracts: {
          existingCredentials: credentialCids,
          existingAllowLists: [],
          existingClawbackReceiverDelegations: endInvestorClawbackDelegations.map(
            (d) => d.contractId,
          ),
          existingRegistrarHoldingOperationsDelegations: endInvestorRegistrarHoldingDelegations.map(
            (d) => d.contractId,
          ),
          existingOrchestratorHoldingOperationsDelegations: [],
          existingServiceUpgradeConsents: endInvestorUpgradeConsents.map((c) => c.contractId),
        },
      },
    };

    await mutate(
      offboardEndInvestorMutation,
      payload,
      SUC_END_INVESTOR_OFFBOARDING,
      ERR_END_INVESTOR_OFFBOARDING,
    );
    handleClose();
  }, [
    endInvestor,
    endInvestorCredentials,
    endInvestorClawbackDelegations,
    endInvestorRegistrarHoldingDelegations,
    endInvestorUpgradeConsents,
    participantServiceContractId,
    handleClose,
    mutate,
    offboardEndInvestorMutation,
  ]);

  const isLoadingQueries =
    credentials.isLoading ||
    holdings.isLoading ||
    clawbackDelegations.isLoading ||
    registrarHoldingOperationsDelegations.isLoading ||
    serviceUpgradeConsents.isLoading;
  const noEndInvestorSet = endInvestor === '';
  const hasHoldings = endInvestorHoldings.length > 0;
  const mutationIsPending = offboardEndInvestorMutation.isPending;

  const disableSubmit = noEndInvestorSet || hasHoldings || mutationIsPending || isLoadingQueries;

  return (
    <FormDialog
      open={open}
      title="Offboard End Investor"
      onCancel={handleClose}
      onSubmit={offboardEndInvestor}
      submitName="Offboard"
      isSubmitDisabled={disableSubmit}
      submitButtonSxProps={{ color: 'error' }}
    >
      {isLoadingQueries ? (
        <Loading />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextInput
            enableTopLabel
            label="End Investor Party ID"
            value={endInvestor}
            setValue={setEndInvestor}
          />
          {endInvestor && (
            <>
              {hasHoldings && (
                <Alert severity="error">
                  Cannot offboard end investor with {endInvestorHoldings.length} holding(s). All
                  holdings must be transferred or redeemed before offboarding.
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary">
                {endInvestorCredentials.length > 0
                  ? `Found ${endInvestorCredentials.length} credential(s) to archive.`
                  : 'No credentials found for this end investor.'}
              </Typography>
            </>
          )}
        </Box>
      )}
    </FormDialog>
  );
};

export default OffboardEndInvestorDialog;
